<template>
  <b-container>
    <b-form-group :disabled="changes_disabled">
      <div>
        <b-form-group>
          <label>
            <b> {{ $t('chosen_table') }} </b>
          </label>
          <b-form-select v-model="table" :options="table_options" />
        </b-form-group>

        <unroll-element
          :title="$t('advanced_options_choice')"
          :available="true"
        >
          <b-form-group>
            <label>
              <b> {{ $t('sensitivity') }} </b>
            </label>
            <range-slider v-model="raw_sensitivity" :options="slider_options" />
          </b-form-group>
          <hr />
          <columns-choice-options
            v-model="cross_validation_columns"
            :main_text="$t('cross_validation_columns')"
            :available_column_names="available_colnames"
          >
            <template #sub_component="props">
              <generic-column-name-edition
                v-bind="props"
                v-on="props.listeners"
              />
            </template>
          </columns-choice-options>
          <hr />
          <b-form-group>
            <label>
              <b> {{ $t('nb_folds') }} </b>
            </label>
            <b-form-input
              v-model="nb_cross_validation_folds"
              type="number"
              :placeholder="$t('enter_number_folds')"
            />
          </b-form-group>
          <hr />
          <columns-choice-options
            v-model="ignored_columns"
            :main_text="$t('ignored_columns')"
            :available_column_names="available_colnames"
          >
            <template #sub_component="props">
              <generic-column-name-edition
                v-bind="props"
                v-on="props.listeners"
              />
            </template>
          </columns-choice-options>
        </unroll-element>
      </div>
    </b-form-group>
  </b-container>
</template>

<i18n>
{
  "en": {
    "sensitivity": "Sensitivity",
    "cross_validation_columns": "Cross validation columns",
    "nb_folds": "Number of cross validation folds",
    "ignored_columns": "Ignored columns",
    "very low": "Very low",
    "low": "Low",
    "medium": "Medium",
    "high": "High",
    "very high": "Very high",
    "enter_number_folds": "Enter the number of folds"
  },
  "fr": {
    "sensitivity": "Sensibilité",
    "cross_validation_columns": "Colonnes pour la validation croisée",
    "nb_folds": "Nombre de folds de validation croisée",
    "ignored_columns": "Colonnes ignorées",
    "very low": "Très basse",
    "low": "Basse",
    "medium": "Moyenne",
    "high": "Haute",
    "very high": "Très haute",
    "enter_number_folds": "Enter le nombre de folds"
  }
}
</i18n>

<script>
import _ from 'lodash'

import axios from 'axios'

import {
  download_result,
  table_columns,
  secure_filename,
  download_file,
} from '@/api/subscriptions.js'
import { GenericPath } from '@/components/object_model.js'

// import {
//   subscription_mixin,
//   brick_mixin,
//   brick_with_single_parent_mixin,
// } from '@/components/mixin.js'
import { global_brick_mixin } from '@/components/mixin.js'
import UnrollElement from '@/components/unroll_element.vue'
import ColumnsChoiceOptions from '@/components/columns_choice_options.vue'
import FilepathSelector from '@/components/filepath_selector.vue'
import GenericColumnNameEdition from '@/components/generic_column_name_edition.vue'
import RangeSlider from '@/components/range_slider.vue'

// *****************************************************************************

import { BasicTableInfoEntity } from '@/components/object_model.js'

export default {
  name: 'BrickProbeColumns',
  components: {
    UnrollElement,
    ColumnsChoiceOptions,
    FilepathSelector,
    GenericColumnNameEdition,
    RangeSlider,
  },
  // mixins: [subscription_mixin, brick_mixin, brick_with_single_parent_mixin],
  mixins: [global_brick_mixin],
  // props: ['brick_id', 'project_id', 'description_name', 'metavariables_dict'],
  data() {
    return {
      table: -1,
      available_colnames: [],
      raw_sensitivity: 2,
      cross_validation_columns: [],
      nb_cross_validation_folds: 5,
      ignored_columns: [],
      sensitivity_scale: ['very low', 'low', 'medium', 'high', 'very high'],
    }
  },
  computed: {
    slider_options() {
      return {
        prettify: t => this.prettify(t),
        min: 0,
        max: this.sensitivity_scale.length - 1,
      }
    },
    sensitivity() {
      return this.sensitivity_scale[this.raw_sensitivity]
    },
    adapted_data() {
      let checked_parent_model_id = []
      if (this.table !== -1) {
        let parent_info_entity_type = new BasicTableInfoEntity()
        parent_info_entity_type.uid = this.table
        checked_parent_model_id = [parent_info_entity_type]
      }
      if (this.table === this.effective_parent_model_id) {
        checked_parent_model_id = this.cur_brick.parents_info
      }
      let adapted = {
        uid: this.brick_id,
        parents_info: checked_parent_model_id,
        sensitivity: this.sensitivity,
        cross_validation_columns: _.cloneDeep(this.cross_validation_columns),
        nb_cross_validation_folds: this.nb_cross_validation_folds,
        ignored_columns: _.cloneDeep(this.ignored_columns),
      }

      _.assignIn(adapted, this.description_name)

      return adapted
    },
    effective_parent_model_id() {
      let parent_model_id = this.cur_brick.parents_info || []
      let res = -1
      if (parent_model_id.length === 1) {
        res = parent_model_id[0]['uid']
      }
      return res
    },
  },
  watch: {
    cur_brick: {
      immediate: true,
      handler(new_val) {
        new_val = new_val || {}

        this.table = this.effective_parent_model_id
        this.raw_sensitivity = _.indexOf(
          this.sensitivity_scale,
          new_val.sensitivity
        )
        this.cross_validation_columns = _.cloneDeep(
          new_val.cross_validation_columns
        )
        this.nb_cross_validation_folds = new_val.nb_cross_validation_folds
        this.ignored_columns = _.cloneDeep(new_val.ignored_columns)
      },
    },
    // sensitivity(nv) {
    //   console.log(nv)
    // },
    table: {
      immediate: true,
      async handler(new_val) {
        this.available_colnames =
          (await table_columns(this.project_id, new_val)).pipe_object || []
        this.columns_wanted = []
      },
    },
  },
  methods: {
    prettify(v) {
      // console.log('prettify', v)
      return this.$t(this.sensitivity_scale[v])
      // return this.$t(v)
    },
  },
}
</script>
